import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { BarChart3, CalendarRange, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import { DIRETORIA_MODULE_TITLE, DIRETORIA_SHOW_MULTAS } from '@/data/diretoriaConstants';
import {
  buildExecutiveCards,
  buildInadimplenciaIndicatorFromCobranca,
  DIRETORIA_SNAPSHOT_INDICATOR_IDS,
  type DiretoriaDashboardStats,
  type DiretoriaFaturamentoMesStats,
  type DiretoriaReceitaProjetadaStats,
} from '@/lib/diretoriaDashboard';
import { getPlaceholderIndicators } from '@/data/diretoriaIndicatorsCatalog';
import { type DiretoriaIndicator } from '@/data/diretoriaTypes';
import { DiretoriaIndicatorAnalysisModal } from '@/components/Diretoria/DiretoriaIndicatorAnalysisModal';
import { DiretoriaExecutiveStrip } from '@/components/Diretoria/DiretoriaExecutiveStrip';
import { DiretoriaPeriodBar } from '@/components/Diretoria/DiretoriaPeriodBar';
import { DiretoriaSparkline } from '@/components/Diretoria/DiretoriaSparkline';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';
import { DiretoriaProjecoesSection } from '@/components/Diretoria/DiretoriaProjecoesSection';
import { DiretoriaIndicatorCardSkeleton } from '@/components/Diretoria/DiretoriaIndicatorCardSkeleton';
import { DiretoriaLocacoesCharts } from '@/components/Diretoria/DiretoriaLocacoesCharts';
import { DiretoriaFaturamentoSection } from '@/components/Diretoria/DiretoriaFaturamentoSection';
import { Badge } from '@/components/ui/Badge';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useDiretoriaSqlCatalog } from '@/hooks/useDiretoriaSqlCatalog';
import {
  buildCustoMotoIndicatorFromErp,
  type DiretoriaCustoPorMotoPayload,
} from '@/lib/diretoriaCustoMotoIndicator';
import {
  buildLocacaoLocalIndicatorFromErp,
  buildMotosAlugadasIndicatorFromErp,
  buildRentabilidadeIndicatorFromErp,
  type DiretoriaFaturamentoBrutoPayload,
  type DiretoriaInadimplenciaPayload,
  type DiretoriaInfracoesPayload,
  type DiretoriaLocacaoLocalPayload,
  type DiretoriaMotosAlugadasPayload,
  type DiretoriaRentabilidadePayload,
} from '@/lib/diretoriaErpIndicatorBuilders';
import {
  buildFaturamentoBrutoIndicatorFromCobranca,
  buildInadimplenciaIndicatorFromCobrancaLocal,
  buildInfracoesIndicatorFromCobranca,
} from '@/lib/diretoriaCobrancaIndicatorBuilders';
import {
  DIRETORIA_EXECUTIVE_QUERY_IDS,
  getIndicatorQueryId,
} from '@/lib/diretoriaQueryIds';
import { fetchDiretoriaErp, parseCompanyCoderp, probeDiretoriaErp } from '@/lib/diretoriaErpFetch';
import { formatDateISO, startOfMonthISO } from '@/lib/diretoriaOficinaFetch';
import { formatPeriodLabel, resolveComparisonPeriod } from '@/lib/diretoriaPeriod';
import { useInViewOnce } from '@/hooks/useInViewOnce';

function DiretoriaSectionFallback({
  error,
  resetError,
}: {
  error?: Error;
  resetError: () => void;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm"
    >
      <p className="font-medium text-destructive">Esta seção falhou ao carregar</p>
      <p className="mt-1 text-muted-foreground">
        {error?.message || 'Erro de interface. O restante da Diretoria continua disponível.'}
      </p>
      <button
        type="button"
        onClick={resetError}
        className="mt-2 font-medium text-primary underline underline-offset-2"
      >
        Tentar novamente
      </button>
    </div>
  );
}

/** Aguarda payload útil: pending/fetching enquanto data ainda é undefined/null (null = soft-fail ERP em cache). */
function isDiretoriaQueryWaiting(
  data: unknown,
  isPending: boolean,
  isFetching: boolean
): boolean {
  if (data != null) return false;
  return isPending || isFetching;
}

interface DiretoriaErpSnapshotData {
  configured: boolean;
  fetchedAt: string;
  ping?: unknown;
  upstreamError?: string;
}

interface DiretoriaErpSnapshotPayload {
  success: boolean;
  data?: DiretoriaErpSnapshotData;
  message?: string;
}

interface DiretoriaMotosDisponiveisMarca {
  idEmpresa: number;
  nomeEmpresa: string;
  marca: string;
  quantidadeMotos: number;
}

interface DiretoriaMotosDisponiveisItem {
  id: number;
  placa: string;
  marca: string;
  modelo: string;
  anoModelo: string;
  anoFabricacao: string;
  cor: string;
  empresa: string;
  idEmpresa: number;
  dataFimUltimaLocacao: string | null;
  custoAcumulado: number;
  receitaAcumulada: number;
}

interface DiretoriaMotosDisponiveisPayload {
  totalDisponiveis: number;
  marcas: DiretoriaMotosDisponiveisMarca[];
  motos?: DiretoriaMotosDisponiveisItem[];
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

interface DiretoriaMotosPorStatusItem {
  idEmpresa: number;
  statusEquipamento: string;
  quantidadeMotos: number;
}

interface DiretoriaMotosPorStatusPayload {
  totalMotos: number;
  status: DiretoriaMotosPorStatusItem[];
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

interface DiretoriaCustoPorMotoPayloadExtended extends DiretoriaCustoPorMotoPayload {
  companyId?: string;
  empresaid?: number;
}

interface DiretoriaCompanyOption {
  id: string;
  name: string;
  coderp?: string | null;
}

const formatCurrencyPtBr = (value: number): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
};

function isoSearchParam(value: string | null): string | undefined {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

export const DiretoriaPage: React.FC = () => {
  const { user } = useAuthStore();
  const setRiqueCompanyId = useRiqueChatStore((s) => s.setCompanyId);
  const setScreenScope = useRiqueChatStore((s) => s.setScreenScope);
  const setPageContext = useRiqueChatStore((s) => s.setPageContext);
  const [searchParams] = useSearchParams();
  const urlCompany = (searchParams.get('companyId') ?? '').trim().slice(0, 64);
  const urlFrom = isoSearchParam(searchParams.get('dataFrom'));
  const urlTo = isoSearchParam(searchParams.get('dataTo'));
  const urlCompareFrom = isoSearchParam(searchParams.get('compareFrom'));
  const urlCompareTo = isoSearchParam(searchParams.get('compareTo'));
  const bootFrom = urlFrom && urlTo ? urlFrom : startOfMonthISO();
  const bootTo = urlFrom && urlTo ? urlTo : formatDateISO(new Date());
  const bootCompare =
    urlCompareFrom && urlCompareTo
      ? { dataFrom: urlCompareFrom, dataTo: urlCompareTo }
      : resolveComparisonPeriod(bootFrom, bootTo);
  const [companyId, setCompanyId] = useState<string>(urlCompany);
  const [open, setOpen] = useState<DiretoriaIndicator | null>(null);
  const [periodFrom, setPeriodFrom] = useState(bootFrom);
  const [periodTo, setPeriodTo] = useState(bootTo);
  const [compareFrom, setCompareFrom] = useState(bootCompare.dataFrom);
  const [compareTo, setCompareTo] = useState(bootCompare.dataTo);

  // Filtros aplicados (só mudam ao clicar em Filtrar) — controlam as queries
  const [appliedCompanyId, setAppliedCompanyId] = useState(companyId);
  const [appliedPeriodFrom, setAppliedPeriodFrom] = useState(periodFrom);
  const [appliedPeriodTo, setAppliedPeriodTo] = useState(periodTo);
  const [appliedCompareFrom, setAppliedCompareFrom] = useState(compareFrom);
  const [appliedCompareTo, setAppliedCompareTo] = useState(compareTo);

  const filtersDirty =
    companyId !== appliedCompanyId ||
    periodFrom !== appliedPeriodFrom ||
    periodTo !== appliedPeriodTo ||
    compareFrom !== appliedCompareFrom ||
    compareTo !== appliedCompareTo;

  const searchKey = searchParams.toString();
  useEffect(() => {
    const from = isoSearchParam(searchParams.get('dataFrom'));
    const to = isoSearchParam(searchParams.get('dataTo'));
    const cid = (searchParams.get('companyId') ?? '').trim().slice(0, 64);
    const cf = isoSearchParam(searchParams.get('compareFrom'));
    const ct = isoSearchParam(searchParams.get('compareTo'));
    if (!from && !to && !cid && !cf && !ct) return;
    if (cid) {
      setCompanyId(cid);
      setAppliedCompanyId(cid);
    }
    if (from && to) {
      setPeriodFrom(from);
      setPeriodTo(to);
      setAppliedPeriodFrom(from);
      setAppliedPeriodTo(to);
    }
    if (cf && ct) {
      setCompareFrom(cf);
      setCompareTo(ct);
      setAppliedCompareFrom(cf);
      setAppliedCompareTo(ct);
    }
  }, [searchKey]);

  useEffect(() => {
    setRiqueCompanyId(appliedCompanyId);
    setScreenScope({
      companyId: appliedCompanyId,
      dataFrom: appliedPeriodFrom,
      dataTo: appliedPeriodTo,
      compareFrom: appliedCompareFrom,
      compareTo: appliedCompareTo,
    });
    const companyLabel = appliedCompanyId ? `empresa ${appliedCompanyId}` : 'todas as empresas';
    setPageContext(
      `Usuário na ${DIRETORIA_MODULE_TITLE}. ${companyLabel}. Período A: ${appliedPeriodFrom} a ${appliedPeriodTo}. Período B (comparativo): ${appliedCompareFrom} a ${appliedCompareTo}. Use as ferramentas da Diretoria com esse recorte.`
    );
    return () => {
      setScreenScope({
        companyId: '',
        dataFrom: '',
        dataTo: '',
        compareFrom: '',
        compareTo: '',
      });
    };
  }, [
    appliedCompanyId,
    appliedPeriodFrom,
    appliedPeriodTo,
    appliedCompareFrom,
    appliedCompareTo,
    setRiqueCompanyId,
    setScreenScope,
    setPageContext,
  ]);

  const applyFilters = () => {
    setAppliedCompanyId(companyId);
    setAppliedPeriodFrom(periodFrom);
    setAppliedPeriodTo(periodTo);
    setAppliedCompareFrom(compareFrom);
    setAppliedCompareTo(compareTo);
  };

  const locacoesSection = useInViewOnce<HTMLDivElement>();
  const faturamentoSection = useInViewOnce<HTMLDivElement>();
  const indicadoresSection = useInViewOnce<HTMLDivElement>();

  const periodLabel = useMemo(
    () => formatPeriodLabel(appliedPeriodFrom, appliedPeriodTo),
    [appliedPeriodFrom, appliedPeriodTo]
  );
  const compareLabel = useMemo(
    () => formatPeriodLabel(appliedCompareFrom, appliedCompareTo),
    [appliedCompareFrom, appliedCompareTo]
  );

  const { data: companies } = useQuery({
    queryKey: ['diretoria-companies', user?.id, user?.role],
    queryFn: async () => {
      const response = await api.get('/companies?page=1&limit=500');
      const payload = response?.data?.data ?? response?.data ?? [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.companies)) return payload.companies;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    },
  });

  const { data: erpSnapshotPayload } = useQuery({
    queryKey: ['diretoria-erp-snapshot'],
    queryFn: async (): Promise<DiretoriaErpSnapshotPayload> => {
      try {
        const response = await api.get<DiretoriaErpSnapshotPayload>('/reports/diretoria/erp-snapshot');
        return response.data;
      } catch (e: unknown) {
        if (isAxiosError(e) && e.response?.status === 503) {
          return { success: true, data: { configured: false, fetchedAt: new Date().toISOString() } };
        }
        if (isAxiosError(e) && e.response?.status === 502) {
          const body = e.response.data as DiretoriaErpSnapshotPayload & { data?: DiretoriaErpSnapshotData };
          return {
            success: false,
            message: typeof body?.message === 'string' ? body.message : 'ERP indisponível.',
            data:
              body?.data ?? {
                configured: true,
                fetchedAt: new Date().toISOString(),
                upstreamError: 'Falha ao consultar o ERP FlyStart.',
              },
          };
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  const {
    data: dashboardStats,
    isError: dashboardError,
    isPending: dashboardPending,
    isFetching: dashboardFetching,
  } = useQuery({
    queryKey: ['diretoria-dashboard', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appliedCompanyId) params.append('companyId', appliedCompanyId);
      params.append('dataFrom', appliedPeriodFrom);
      params.append('dataTo', appliedPeriodTo);
      const response = await api.get(`/reports/dashboard?${params.toString()}`);
      return response.data.data as DiretoriaDashboardStats;
    },
    retry: 1,
    staleTime: 30_000,
  });

  const { getSql, getSource } = useDiretoriaSqlCatalog(
    appliedCompanyId,
    appliedPeriodFrom,
    appliedPeriodTo,
    appliedCompareFrom,
    appliedCompareTo
  );

  const { data: faturamentoMes } = useQuery({
    queryKey: ['diretoria-faturamento-mes', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo, appliedCompareFrom, appliedCompareTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaFaturamentoMesStats & { querySql?: string }>(
        '/reports/diretoria/faturamento-mes',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedCompareFrom,
        appliedCompareTo
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const { data: receitaProjetada } = useQuery({
    queryKey: ['diretoria-receita-projetada', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo, appliedCompareFrom, appliedCompareTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaReceitaProjetadaStats & { querySql?: string }>(
        '/reports/diretoria/receita-projetada',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedCompareFrom,
        appliedCompareTo
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: motosDisponiveis,
    isPending: motosDisponiveisPending,
    isFetching: motosDisponiveisFetching,
  } = useQuery({
    queryKey: ['diretoria-motos-disponiveis', appliedCompanyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaMotosDisponiveisPayload>('/reports/diretoria/motos-disponiveis', appliedCompanyId),
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: motosPorStatus,
    isPending: motosPorStatusPending,
    isFetching: motosPorStatusFetching,
  } = useQuery({
    queryKey: ['diretoria-motos-por-status', appliedCompanyId],
    queryFn: () => fetchDiretoriaErp<DiretoriaMotosPorStatusPayload>('/reports/diretoria/motos-por-status', appliedCompanyId),
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: custoPorMoto,
    isPending: custoPorMotoPending,
    isFetching: custoPorMotoFetching,
  } = useQuery({
    queryKey: ['diretoria-custo-por-moto', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaCustoPorMotoPayloadExtended>(
        '/reports/diretoria/custo-por-moto',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo
      ),
    enabled: locacoesSection.inView,
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: faturamentoBruto,
    isPending: faturamentoBrutoPending,
    isFetching: faturamentoBrutoFetching,
  } = useQuery({
    queryKey: ['diretoria-faturamento-bruto', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo, appliedCompareFrom, appliedCompareTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaFaturamentoBrutoPayload>(
        '/reports/diretoria/faturamento-bruto',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedCompareFrom,
        appliedCompareTo
      ),
    enabled: false,
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: inadimplenciaCobranca,
    isPending: inadimplenciaPending,
    isFetching: inadimplenciaFetching,
  } = useQuery({
    queryKey: ['diretoria-inadimplencia-cobranca', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaInadimplenciaPayload>(
        '/reports/diretoria/inadimplencia',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: infracoesCobranca,
    isPending: infracoesPending,
    isFetching: infracoesFetching,
  } = useQuery({
    queryKey: ['diretoria-infracoes-cobranca', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: () =>
      fetchDiretoriaErp<
        DiretoriaInfracoesPayload & {
          mensal?: Array<{ name: string; qtdPagas: number; valorPagas: number }>;
          porEmpresa?: Array<{
            nomeEmpresa: string;
            qtdPagas: number;
            qtdVencidas: number;
            qtdAVencer: number;
            valorPagas: number;
            valorAberto: number;
          }>;
        }
      >('/reports/diretoria/infracoes', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo),
    enabled: DIRETORIA_SHOW_MULTAS && faturamentoSection.inView,
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: faturamentoProduto,
    isPending: faturamentoProdutoPending,
    isFetching: faturamentoProdutoFetching,
  } = useQuery({
    queryKey: ['diretoria-faturamento-produto', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: () =>
      fetchDiretoriaErp<{
        itens: Array<{
          name: string;
          produto: string;
          nomeEmpresa: string;
          totalPago: number;
          qtdLocacoes?: number;
        }>;
        porProduto?: Array<{
          produto: string;
          totalPago: number;
          qtdLocacoes: number;
          ticketMedio: number;
        }>;
        produtos: string[];
        dimensaoProduto?: string;
        querySql?: string;
      }>('/reports/diretoria/faturamento-produto', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo),
    enabled: faturamentoSection.inView,
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: motosAlugadas,
    isPending: motosAlugadasPending,
    isFetching: motosAlugadasFetching,
  } = useQuery({
    queryKey: ['diretoria-motos-alugadas', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo, appliedCompareFrom, appliedCompareTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaMotosAlugadasPayload>(
        '/reports/diretoria/motos-alugadas',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedCompareFrom,
        appliedCompareTo
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: rentabilidadeMoto,
    isPending: rentabilidadeMotoPending,
    isFetching: rentabilidadeMotoFetching,
  } = useQuery({
    queryKey: ['diretoria-rentabilidade-moto', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo, appliedCompareFrom, appliedCompareTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaRentabilidadePayload>(
        '/reports/diretoria/rentabilidade-moto',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedCompareFrom,
        appliedCompareTo
      ),
    enabled: indicadoresSection.inView,
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: locacaoLocal,
    isPending: locacaoLocalPending,
    isFetching: locacaoLocalFetching,
  } = useQuery({
    queryKey: ['diretoria-locacao-local', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo, appliedCompareFrom, appliedCompareTo],
    queryFn: () =>
      fetchDiretoriaErp<DiretoriaLocacaoLocalPayload>(
        '/reports/diretoria/locacao-local',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        appliedCompareFrom,
        appliedCompareTo
      ),
    enabled: indicadoresSection.inView,
    retry: 1,
    staleTime: 60_000,
  });

  const metaAno = Number(appliedPeriodTo.slice(0, 4)) || new Date().getFullYear();
  const metaMes = Number(appliedPeriodTo.slice(5, 7)) || new Date().getMonth() + 1;

  type LocacoesAnalyticsPayload = {
    criadas: number;
    canceladas: number;
    canceladasLt30: number;
    canceladasLt60: number;
    canceladasLt90: number;
    locacoesAtivas: number;
    canceladasMesRef: number;
    turnoverPct: number | null;
    mensal: Array<{ ano: number; mes: number; name: string; novas: number; canceladas: number }>;
    mensalAnos: number[];
    mensalComparativo: Array<{ mes: number; name: string } & Record<string, number | string>>;
    diario: Array<{ dia: string; name: string; novas: number; canceladas: number }>;
    topModeloMensal: Array<{
      ano: number;
      mes: number;
      name: string;
      marca: string;
      modelo: string;
      anoModelo?: string;
      quantidade: number;
    }>;
    topModeloSeries: Array<{
      key: string;
      name: string;
      marca?: string;
      modelo?: string;
      anoModelo?: string;
    }>;
    topModeloPorAno?: Array<{
      ano: number;
      pontos: Array<{ mes: number; name: string } & Record<string, number | string>>;
    }>;
    topModeloComparativo: Array<{ mes: number; name: string } & Record<string, number | string>>;
    topPlacas: Array<{
      placa: string;
      marca: string;
      modelo: string;
      anoModelo?: string;
      empresa: string;
      idEmpresa: string;
      idEquipamento?: number;
      quantidade: number;
      totalFaturado?: number;
      totalCusto?: number;
      custoCompra?: number;
      diferenca?: number;
      diferencaPct?: number | null;
    }>;
    tiposLocacao: Array<{ tipo: string; quantidade: number; canceladas: number }>;
    querySql?: string;
  };

  const { data: locacoesKpis } = useQuery({
    queryKey: ['diretoria-locacoes-analytics', 'kpis', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: () =>
      fetchDiretoriaErp<LocacoesAnalyticsPayload>(
        '/reports/diretoria/locacoes-analytics',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        undefined,
        undefined,
        { scope: 'kpis' }
      ),
    retry: 1,
    staleTime: 60_000,
  });

  const {
    data: locacoesCharts,
    isPending: locacoesChartsPending,
    isFetching: locacoesChartsFetching,
  } = useQuery({
    queryKey: ['diretoria-locacoes-analytics', 'charts', appliedCompanyId, appliedPeriodFrom, appliedPeriodTo],
    queryFn: () =>
      fetchDiretoriaErp<LocacoesAnalyticsPayload>(
        '/reports/diretoria/locacoes-analytics',
        appliedCompanyId,
        appliedPeriodFrom,
        appliedPeriodTo,
        undefined,
        undefined,
        { scope: 'charts' }
      ),
    enabled: locacoesSection.inView,
    retry: 1,
    staleTime: 60_000,
  });

  const locacoesChartsLoading =
    locacoesSection.inView &&
    isDiretoriaQueryWaiting(locacoesCharts, locacoesChartsPending, locacoesChartsFetching);

  const locacoesAnalytics = useMemo((): LocacoesAnalyticsPayload | null => {
    if (!locacoesKpis && !locacoesCharts) return null;
    return {
      criadas: locacoesKpis?.criadas ?? 0,
      canceladas: locacoesKpis?.canceladas ?? 0,
      canceladasLt30: locacoesKpis?.canceladasLt30 ?? 0,
      canceladasLt60: locacoesKpis?.canceladasLt60 ?? 0,
      canceladasLt90: locacoesKpis?.canceladasLt90 ?? 0,
      locacoesAtivas: locacoesKpis?.locacoesAtivas ?? 0,
      canceladasMesRef: locacoesKpis?.canceladasMesRef ?? 0,
      turnoverPct: locacoesKpis?.turnoverPct ?? null,
      tiposLocacao: locacoesKpis?.tiposLocacao ?? [],
      mensal: locacoesCharts?.mensal ?? [],
      mensalAnos: locacoesCharts?.mensalAnos ?? [],
      mensalComparativo: locacoesCharts?.mensalComparativo ?? [],
      diario: locacoesCharts?.diario ?? [],
      topModeloMensal: locacoesCharts?.topModeloMensal ?? [],
      topModeloSeries: locacoesCharts?.topModeloSeries ?? [],
      topModeloPorAno: locacoesCharts?.topModeloPorAno ?? [],
      topModeloComparativo: locacoesCharts?.topModeloComparativo ?? [],
      topPlacas: locacoesCharts?.topPlacas ?? [],
      querySql: locacoesCharts?.querySql ?? locacoesKpis?.querySql,
    };
  }, [locacoesKpis, locacoesCharts]);

  const { data: diretoriaMetas } = useQuery({
    queryKey: ['diretoria-metas', appliedCompanyId, metaAno, metaMes],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appliedCompanyId) params.set('companyId', appliedCompanyId);
      params.set('ano', String(metaAno));
      params.set('mes', String(metaMes));
      const res = await api.get(`/reports/diretoria/metas?${params.toString()}`);
      return res.data.data as {
        companyId: string;
        ano: number;
        mes: number;
        locoesCriadas: number | null;
        cancelamentos: number | null;
      };
    },
    staleTime: 30_000,
  });

  const companyList = useMemo(
    () => (Array.isArray(companies) ? (companies as DiretoriaCompanyOption[]) : []),
    [companies]
  );

  const selectedCompany = useMemo(
    () => (appliedCompanyId ? companyList.find((c) => c.id === appliedCompanyId) : undefined),
    [appliedCompanyId, companyList]
  );

  const missingCoderp = Boolean(
    appliedCompanyId && selectedCompany && parseCompanyCoderp(selectedCompany.coderp) == null
  );

  const { data: erpProbe } = useQuery({
    queryKey: ['diretoria-erp-probe', appliedCompanyId],
    queryFn: () => probeDiretoriaErp(appliedCompanyId),
    enabled: erpSnapshotPayload?.data?.configured !== false && !missingCoderp,
    retry: false,
    staleTime: 30_000,
  });

  const companyLabel = useMemo(() => {
    if (!appliedCompanyId) return 'Todas as empresas';
    return selectedCompany?.name ?? appliedCompanyId;
  }, [appliedCompanyId, selectedCompany]);

  const baseIndicators = useMemo(() => getPlaceholderIndicators(), []);

  const indicators = useMemo(() => {
    const safeBuild = (
      id: string,
      build: () => DiretoriaIndicator,
      fallback: DiretoriaIndicator
    ): DiretoriaIndicator => {
      try {
        return build();
      } catch (err) {
        console.error(`Diretoria: falha ao montar indicador ${id}`, err);
        return fallback;
      }
    };

    return baseIndicators.map((indicator) => {
      if (indicator.id === 'custo-moto' && custoPorMoto) {
        return safeBuild(indicator.id, () =>
          buildCustoMotoIndicatorFromErp(indicator, custoPorMoto, formatCurrencyPtBr), indicator);
      }
      if (indicator.id === 'faturamento-bruto' && faturamentoBruto) {
        return safeBuild(indicator.id, () =>
          buildFaturamentoBrutoIndicatorFromCobranca(indicator, faturamentoBruto, formatCurrencyPtBr), indicator);
      }
      if (indicator.id === 'inadimplencia') {
        if (inadimplenciaCobranca) {
          return safeBuild(indicator.id, () =>
            buildInadimplenciaIndicatorFromCobrancaLocal(
              indicator,
              inadimplenciaCobranca,
              formatCurrencyPtBr
            ),
            indicator
          );
        }
        if (dashboardStats && !dashboardError) {
          return safeBuild(indicator.id, () =>
            buildInadimplenciaIndicatorFromCobranca(indicator, dashboardStats, formatCurrencyPtBr), indicator);
        }
      }
      if (indicator.id === 'infracoes' && infracoesCobranca) {
        return safeBuild(indicator.id, () =>
          buildInfracoesIndicatorFromCobranca(indicator, infracoesCobranca, formatCurrencyPtBr), indicator);
      }
      if (indicator.id === 'motos-alugadas' && motosAlugadas) {
        return safeBuild(indicator.id, () =>
          buildMotosAlugadasIndicatorFromErp(indicator, motosAlugadas), indicator);
      }
      if (indicator.id === 'rentabilidade-moto' && rentabilidadeMoto) {
        return safeBuild(indicator.id, () =>
          buildRentabilidadeIndicatorFromErp(indicator, rentabilidadeMoto, formatCurrencyPtBr), indicator);
      }
      if (indicator.id === 'locacao-local' && locacaoLocal) {
        return safeBuild(indicator.id, () =>
          buildLocacaoLocalIndicatorFromErp(indicator, locacaoLocal), indicator);
      }
      if (indicator.id === 'motos-status' && motosPorStatus) {
        return safeBuild(
          indicator.id,
          () => {
        const formatStatusLabel = (raw: string): string => {
          const key = raw.trim().toLowerCase();
          const labels: Record<string, string> = {
            alugada: 'Alugada',
            disponivel: 'Disponível',
            vendida: 'Vendida',
            em_manutencao: 'Em manutenção',
            furto_roubo: 'Furto / roubo',
            apropriacao_indebita: 'Apropriação indébita',
            apreendida: 'Apreendida',
            perda_total: 'Perda total',
            reservada: 'Reservada',
            indisponivel: 'Indisponível',
          };
          if (labels[key]) return labels[key];
          return raw
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
        };
        const statusMap = new Map<string, number>();
        for (const row of motosPorStatus.status ?? []) {
          const key = row.statusEquipamento || 'Sem status';
          statusMap.set(key, (statusMap.get(key) ?? 0) + row.quantidadeMotos);
        }
        const total = motosPorStatus.totalMotos || 0;
        const statusRows = Array.from(statusMap.entries())
          .map(([raw, value]) => ({
            raw,
            name: formatStatusLabel(raw),
            value,
            pct: total > 0 ? (value / total) * 100 : 0,
          }))
          .sort((a, b) => b.value - a.value);
        const topStatus = statusRows[0];
        const criticalStatus = statusRows
          .filter((row) => /apropr|furto|roubo|apreend/i.test(row.raw))
          .reduce((sum, row) => sum + row.value, 0);
        return {
          ...indicator,
          shortDescription: 'Distribuição da frota por status operacional (ERP FlyStart).',
          kpis: [
            {
              label: 'Total na base',
              value: String(total),
              hint: 'ERP FlyStart',
              tone: 'neutral',
            },
            {
              label: 'Maior status',
              value: topStatus
                ? `${topStatus.name} (${topStatus.pct.toFixed(1)}%)`
                : '—',
              hint: 'ERP FlyStart',
              tone: /apropr|furto|roubo|apreend/i.test(topStatus?.raw ?? '') ? 'bad' : 'neutral',
            },
            {
              label: 'Situações críticas',
              value: String(criticalStatus),
              hint: 'Apropriação + Furto/Roubo + Apreendida',
              tone: criticalStatus === 0 ? 'good' : 'bad',
            },
          ],
          chartKind: 'bar',
          chartTitle: 'Composição da frota por status',
          series: statusRows.map((row) => ({
            name: row.name,
            valor: row.value,
            valor2: Math.round(row.pct * 10) / 10,
          })),
          barSeriesKey: 'valor',
          seriesLabels: { valor: 'Qtd.', valor2: '% frota' },
          tableHeaders: ['Status', 'Qtd.', '% frota'],
          tableRows: statusRows.map((row) => [
            row.name,
            String(row.value),
            `${row.pct.toFixed(1)}%`,
          ]),
          footnote:
            'ERP FlyStart: ativo=1, deleted_at IS NULL. Colunas ordenadas por quantidade (rótulos amigáveis); detalhes na tabela.',
          querySql: motosPorStatus.querySql,
          dataSource: 'erp',
          hasData: statusRows.length > 0,
        } satisfies DiretoriaIndicator;
          },
          indicator
        );
      }
      if (indicator.id === 'motos-disponiveis' && motosDisponiveis) {
        return safeBuild(
          indicator.id,
          () => {
        const marcas = [...(motosDisponiveis.marcas ?? [])].sort((a, b) => {
          const byEmpresa = a.nomeEmpresa.localeCompare(b.nomeEmpresa, 'pt-BR');
          if (byEmpresa !== 0) return byEmpresa;
          const byMarca = a.marca.localeCompare(b.marca, 'pt-BR');
          if (byMarca !== 0) return byMarca;
          return b.quantidadeMotos - a.quantidadeMotos;
        });
        const topPorQtd = [...marcas].sort((a, b) => b.quantidadeMotos - a.quantidadeMotos)[0];
        const shortEmpresa = (nome: string) =>
          nome.replace(/^ZEH\s*MOTOCA\s+/i, '').trim() || nome;
        const labelEmpresaMarca = (row: (typeof marcas)[number]) =>
          `${shortEmpresa(row.nomeEmpresa)} · ${row.marca}`;
        const fmtDateBr = (iso: string | null | undefined): string => {
          if (!iso) return '—';
          const [y, m, d] = iso.split('-');
          if (!y || !m || !d) return iso;
          return `${d}/${m}/${y}`;
        };
        const motos = [...(motosDisponiveis.motos ?? [])].sort((a, b) => {
          const byEmp = a.empresa.localeCompare(b.empresa, 'pt-BR');
          if (byEmp !== 0) return byEmp;
          return a.placa.localeCompare(b.placa, 'pt-BR');
        });
        const anoModeloLabel = (m: (typeof motos)[number]) => {
          const modelo = m.modelo || '—';
          const ano = m.anoModelo || m.anoFabricacao;
          return ano ? `${modelo} · ${ano}` : modelo;
        };
        return {
          ...indicator,
          shortDescription: 'Motos disponíveis por empresa e marca (ERP FlyStart).',
          kpis: [
            {
              label: 'Disponíveis hoje',
              value: String(motosDisponiveis.totalDisponiveis),
              hint: 'ERP FlyStart',
              tone: motosDisponiveis.totalDisponiveis > 0 ? 'good' : 'neutral',
            },
            {
              label: 'Top (empresa · marca)',
              value: topPorQtd
                ? `${labelEmpresaMarca(topPorQtd)} (${topPorQtd.quantidadeMotos})`
                : '—',
              hint: 'ERP FlyStart',
              tone: 'neutral',
            },
            {
              label: 'Combinações',
              value: String(marcas.length),
              hint: 'Empresa × marca',
              tone: 'neutral',
            },
          ],
          chartKind: 'barHorizontal',
          chartTitle: 'Disponíveis por empresa · marca',
          series: marcas
            .slice()
            .sort((a, b) => b.quantidadeMotos - a.quantidadeMotos)
            .slice(0, 12)
            .map((row) => ({
              name: labelEmpresaMarca(row),
              valor: row.quantidadeMotos,
            })),
          barSeriesKey: 'valor',
          seriesLabels: { valor: 'Disponíveis' },
          tableHeaders: ['Empresa', 'Marca', 'Disponíveis'],
          tableRows: marcas.map((row) => [row.nomeEmpresa, row.marca, String(row.quantidadeMotos)]),
          detailTableTitle: `Motos disponíveis (${motos.length})`,
          detailTableHeaders: [
            'Placa',
            'Marca',
            'Ano e modelo',
            'Cor',
            'Última finalização',
            'Custo acum.',
            'Receita acum.',
            'Empresa',
          ],
          detailTableRows: motos.map((m) => [
            m.placa,
            m.marca,
            anoModeloLabel(m),
            m.cor || '—',
            fmtDateBr(m.dataFimUltimaLocacao),
            formatCurrencyPtBr(m.custoAcumulado),
            formatCurrencyPtBr(m.receitaAcumulada),
            m.empresa,
          ]),
          footnote:
            'ERP FlyStart: status_equipamento=disponivel, ativo=1. Gráfico top 12 por empresa · marca. Listagem: custo = OS+pagar; receita = baixas RECEBER; cor/última finalização via PortalFly (cancelamento ou fim esperado).',
          querySql: motosDisponiveis.querySql,
          dataSource: 'erp',
          hasData: marcas.length > 0 || motos.length > 0,
        } satisfies DiretoriaIndicator;
          },
          indicator
        );
      }
      return indicator;
    });
  }, [
    baseIndicators,
    custoPorMoto,
    faturamentoBruto,
    inadimplenciaCobranca,
    dashboardStats,
    dashboardError,
    infracoesCobranca,
    motosAlugadas,
    rentabilidadeMoto,
    locacaoLocal,
    motosDisponiveis,
    motosPorStatus,
  ]);

  const HIDDEN_INDICATOR_CARD_IDS = useMemo(
    () => new Set(['faturamento-bruto', 'inadimplencia']),
    []
  );

  const FATURAMENTO_INDICATOR_IDS = useMemo(() => {
    const ids = ['faturamento-bruto', 'inadimplencia'];
    if (DIRETORIA_SHOW_MULTAS) ids.push('infracoes');
    return new Set(ids);
  }, []);

  const indicatorLoadingById = useMemo(() => {
    const inadReady = Boolean(inadimplenciaCobranca) || Boolean(dashboardStats && !dashboardError);
    const map: Record<string, boolean> = {
      'custo-moto': isDiretoriaQueryWaiting(custoPorMoto, custoPorMotoPending, custoPorMotoFetching),
      'faturamento-bruto': isDiretoriaQueryWaiting(
        faturamentoBruto,
        faturamentoBrutoPending,
        faturamentoBrutoFetching
      ),
      inadimplencia:
        !inadReady &&
        (isDiretoriaQueryWaiting(inadimplenciaCobranca, inadimplenciaPending, inadimplenciaFetching) ||
          isDiretoriaQueryWaiting(dashboardStats, dashboardPending, dashboardFetching)),
      infracoes: isDiretoriaQueryWaiting(infracoesCobranca, infracoesPending, infracoesFetching),
      'faturamento-produto': isDiretoriaQueryWaiting(
        faturamentoProduto,
        faturamentoProdutoPending,
        faturamentoProdutoFetching
      ),
      'motos-alugadas': isDiretoriaQueryWaiting(motosAlugadas, motosAlugadasPending, motosAlugadasFetching),
      'rentabilidade-moto': isDiretoriaQueryWaiting(
        rentabilidadeMoto,
        rentabilidadeMotoPending,
        rentabilidadeMotoFetching
      ),
      'locacao-local': isDiretoriaQueryWaiting(locacaoLocal, locacaoLocalPending, locacaoLocalFetching),
      'motos-status': isDiretoriaQueryWaiting(motosPorStatus, motosPorStatusPending, motosPorStatusFetching),
      'motos-disponiveis': isDiretoriaQueryWaiting(
        motosDisponiveis,
        motosDisponiveisPending,
        motosDisponiveisFetching
      ),
    };
    return map;
  }, [
    custoPorMotoPending,
    custoPorMotoFetching,
    custoPorMoto,
    faturamentoBrutoPending,
    faturamentoBrutoFetching,
    faturamentoBruto,
    inadimplenciaCobranca,
    inadimplenciaPending,
    inadimplenciaFetching,
    dashboardStats,
    dashboardError,
    dashboardPending,
    dashboardFetching,
    infracoesPending,
    infracoesFetching,
    infracoesCobranca,
    faturamentoProduto,
    faturamentoProdutoPending,
    faturamentoProdutoFetching,
    motosAlugadasPending,
    motosAlugadasFetching,
    motosAlugadas,
    rentabilidadeMotoPending,
    rentabilidadeMotoFetching,
    rentabilidadeMoto,
    locacaoLocalPending,
    locacaoLocalFetching,
    locacaoLocal,
    motosPorStatusPending,
    motosPorStatusFetching,
    motosPorStatus,
    motosDisponiveisPending,
    motosDisponiveisFetching,
    motosDisponiveis,
  ]);

  const faturamentoIndicators = useMemo(
    () =>
      indicators.filter(
        (ind) =>
          FATURAMENTO_INDICATOR_IDS.has(ind.id) &&
          !HIDDEN_INDICATOR_CARD_IDS.has(ind.id) &&
          (DIRETORIA_SHOW_MULTAS || ind.id !== 'infracoes')
      ),
    [indicators, FATURAMENTO_INDICATOR_IDS, HIDDEN_INDICATOR_CARD_IDS]
  );

  const outrosIndicators = useMemo(
    () =>
      indicators.filter(
        (ind) =>
          !FATURAMENTO_INDICATOR_IDS.has(ind.id) &&
          !HIDDEN_INDICATOR_CARD_IDS.has(ind.id) &&
          (DIRETORIA_SHOW_MULTAS || ind.id !== 'infracoes')
      ),
    [indicators, FATURAMENTO_INDICATOR_IDS, HIDDEN_INDICATOR_CARD_IDS]
  );

  const executiveCards = useMemo(
    () => buildExecutiveCards(dashboardStats, faturamentoMes, receitaProjetada, periodLabel, compareLabel),
    [dashboardStats, faturamentoMes, receitaProjetada, periodLabel, compareLabel]
  );

  const erp = erpSnapshotPayload;
  const erpOk =
    Boolean(erp?.success && erp.data?.configured && erp.data.ping != null && !erp.data.upstreamError);
  const erpDegraded = Boolean(erp && erp.success === false && erp.data?.configured);
  const erpNotConfigured = Boolean(erp?.data && !erp.data.configured);

  const resolveExecutiveSql = (cardId: string) => {
    const queryId = DIRETORIA_EXECUTIVE_QUERY_IDS[cardId];
    if (!queryId) return {};
    const card = executiveCards.find((c) => c.id === cardId);
    const override =
      cardId === 'exec-faturamento-mes'
        ? faturamentoMes?.querySql
        : cardId === 'exec-receita-projetada'
          ? receitaProjetada?.querySql
          : undefined;
    return { sql: getSql(queryId, override), source: getSource(queryId), title: card?.label };
  };

  const resolveIndicatorSql = (ind: DiretoriaIndicator) => {
    const queryId = getIndicatorQueryId(ind.id);
    return {
      sql: getSql(queryId, ind.querySql),
      source:
        ind.dataSource === 'cobranca'
          ? ('cobranca' as const)
          : getSource(queryId),
      title: ind.title,
    };
  };

  return (
    <>
      <Helmet>
        <title>{DIRETORIA_MODULE_TITLE} — PortalFly</title>
      </Helmet>

      <div className="mx-auto w-full max-w-[min(100%,1680px)] space-y-5 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-6 lg:px-8">
        <div className="flex flex-col gap-1">
          <div className="mb-0.5 flex items-center gap-2 text-muted-foreground">
            <BarChart3 className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-xs font-medium uppercase tracking-wide">Módulo</span>
          </div>
          <h1 className="font-heading text-heading-3 font-bold text-foreground sm:text-heading-2">
            {DIRETORIA_MODULE_TITLE}
          </h1>
        </div>

        <section
          aria-label="Filtros da Diretoria"
          className="sticky top-0 z-20 -mx-3 border-y border-border/80 bg-background/95 px-3 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:mx-0 sm:rounded-xl sm:border sm:px-4 sm:py-4 sm:shadow-soft-sm"
        >
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(14rem,18rem)] lg:items-start lg:gap-4">
            <DiretoriaPeriodBar
              dataFrom={periodFrom}
              dataTo={periodTo}
              compareFrom={compareFrom}
              compareTo={compareTo}
              hideSummary
              onChange={(from, to, compFrom, compTo) => {
                setPeriodFrom(from);
                setPeriodTo(to);
                setCompareFrom(compFrom);
                setCompareTo(compTo);
              }}
            />
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5">
                <span className="text-[10px] font-semibold uppercase leading-none tracking-wide text-muted-foreground">
                  Empresa
                </span>
                <select
                  className={cn(
                    'h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm text-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  <option value="">Todas as empresas</option>
                  {companyList.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                      {parseCompanyCoderp(company.coderp) == null ? ' (sem Código ERP)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                className="w-full"
                disabled={!filtersDirty}
                onClick={applyFilters}
              >
                <Filter className="mr-1.5 h-4 w-4" aria-hidden />
                Filtrar
              </Button>
            </div>
          </div>

          <p className="mt-3 flex items-start gap-1.5 border-t border-border/60 pt-2.5 text-[11px] leading-snug text-muted-foreground">
            <CalendarRange className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">
              <span className="font-medium text-foreground">{companyLabel}</span>
              {' · '}
              {periodLabel}
              {' · vs '}
              {compareLabel}
              {filtersDirty ? (
                <span className="ml-1 text-amber-700 dark:text-amber-300">
                  · filtros alterados — clique em Filtrar
                </span>
              ) : null}
            </span>
          </p>
        </section>

        {erpOk ? (
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200"
            role="status"
          >
            ERP FlyStart: conexão ativa. Última verificação:{' '}
            <span className="tabular-nums">{new Date(erp!.data!.fetchedAt).toLocaleString('pt-BR')}</span>.
          </div>
        ) : null}
        {erpNotConfigured ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            ERP FlyStart não configurado. Indicadores de frota/financeiro ERP exibem &quot;—&quot; até a integração SQL
            ser habilitada.
          </div>
        ) : null}
        {erpDegraded ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            ERP FlyStart indisponível ({erp?.message ?? erp?.data?.upstreamError ?? 'erro'}). Indicadores ERP
            permanecem vazios até a conexão voltar.
          </div>
        ) : null}
        {missingCoderp ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            A empresa <span className="font-medium">{companyLabel}</span> não possui{' '}
            <span className="font-medium">Código ERP</span> (empresaid FlyStart) configurado. Preencha em{' '}
            <Link to="/companies" className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-50">
              Empresas
            </Link>{' '}
            → editar → campo &quot;Código ERP&quot;. Enquanto isso, os indicadores ERP exibem &quot;—&quot; para este
            filtro (o ping de conexão pode estar OK mesmo sem código de empresa).
          </div>
        ) : null}
        {!missingCoderp && erpProbe && !erpProbe.ok && erpOk ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
            role="alert"
          >
            Conexão ERP ativa, mas a consulta de indicadores falhou
            {erpProbe.error.status ? ` (HTTP ${erpProbe.error.status})` : ''}:{' '}
            <span className="font-medium">{erpProbe.error.message}</span>.
            {appliedCompanyId ? (
              <>
                {' '}
                Confira o Código ERP em{' '}
                <Link
                  to="/companies"
                  className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-50"
                >
                  Empresas
                </Link>{' '}
                ou teste o filtro &quot;Todas as empresas&quot;.
              </>
            ) : null}
          </div>
        ) : null}

        <DiretoriaExecutiveStrip cards={executiveCards} resolveSql={resolveExecutiveSql} />

        <ErrorBoundary fallback={DiretoriaSectionFallback}>
          <DiretoriaProjecoesSection
            companyId={appliedCompanyId}
            periodFrom={appliedPeriodFrom}
            periodTo={appliedPeriodTo}
            periodLabel={periodLabel}
            criadas={locacoesAnalytics?.criadas ?? motosAlugadas?.novasMes ?? 0}
            canceladas={locacoesAnalytics?.canceladas ?? motosAlugadas?.canceladasMes ?? 0}
            canceladasLt30={locacoesAnalytics?.canceladasLt30 ?? 0}
            canceladasLt60={locacoesAnalytics?.canceladasLt60 ?? 0}
            canceladasLt90={locacoesAnalytics?.canceladasLt90 ?? 0}
            locacoesAtivas={locacoesAnalytics?.locacoesAtivas ?? 0}
            canceladasMesRef={locacoesAnalytics?.canceladasMesRef}
            turnoverPct={locacoesAnalytics?.turnoverPct ?? null}
            metas={diretoriaMetas}
            mensal={motosAlugadas?.mensal ?? locacoesAnalytics?.mensal ?? []}
            tiposLocacao={locacoesAnalytics?.tiposLocacao ?? []}
          />
        </ErrorBoundary>

        <ErrorBoundary fallback={DiretoriaSectionFallback}>
          <div ref={locacoesSection.ref}>
            <h2 className="mb-3 font-heading text-lg font-semibold text-foreground">Locações</h2>
            <DiretoriaLocacoesCharts
              data={locacoesAnalytics ?? undefined}
              motosCusto={custoPorMoto?.motos}
              companyId={appliedCompanyId || undefined}
              isLoading={locacoesChartsLoading}
            />
          </div>
        </ErrorBoundary>

        <ErrorBoundary fallback={DiretoriaSectionFallback}>
          <div ref={faturamentoSection.ref}>
            <DiretoriaFaturamentoSection
              indicators={faturamentoIndicators}
              loadingById={indicatorLoadingById}
              onOpenIndicator={setOpen}
              resolveIndicatorSql={resolveIndicatorSql}
              formatCurrency={formatCurrencyPtBr}
              companyId={appliedCompanyId}
              periodFrom={appliedPeriodFrom}
              periodTo={appliedPeriodTo}
              faturamentoProdutoSql={getSql('cobranca-faturamento-produto', faturamentoProduto?.querySql)}
              inadimplencia={inadimplenciaCobranca}
              faturamentoProduto={faturamentoProduto}
              infracoesExt={DIRETORIA_SHOW_MULTAS ? infracoesCobranca : null}
            />
          </div>
        </ErrorBoundary>

        <div ref={indicadoresSection.ref}>
          <h2 className="mb-3 font-heading text-lg font-semibold text-foreground">Indicadores</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {outrosIndicators.map((ind) => {
              if (indicatorLoadingById[ind.id]) {
                return (
                  <DiretoriaIndicatorCardSkeleton
                    key={ind.id}
                    title={ind.title}
                    description={ind.shortDescription}
                  />
                );
              }
              const kpis = ind.kpis ?? [];
              const pk = Math.min(ind.primaryKpiIndex ?? 0, Math.max(0, kpis.length - 1));
              const primary = kpis[pk];
              const secondary = kpis.filter((_, i) => i !== pk);
              return (
                <Card key={ind.id} className="relative h-full border-border/80 hover:shadow-card-hover">
                  <div className="absolute right-2 top-2 z-10">
                    <DiretoriaSqlButton {...resolveIndicatorSql(ind)} stopPropagation />
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(ind)}
                    className="w-full text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <CardHeader className="pb-2 pr-10">
                      <CardTitle className="text-base leading-snug">{ind.title}</CardTitle>
                      <p className="text-xs text-muted-foreground line-clamp-2">{ind.shortDescription}</p>
                      {DIRETORIA_SNAPSHOT_INDICATOR_IDS.has(ind.id) ? (
                        <Badge variant="outline" className="mt-1 w-fit text-[10px] font-normal">
                          Situação atual · não filtrado por período
                        </Badge>
                      ) : null}
                    </CardHeader>
                    <CardContent className="space-y-3 pt-0">
                      {primary ? (
                        <div>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {primary.label}
                          </p>
                          <p
                            className={cn(
                              'font-heading text-xl font-bold tabular-nums break-words sm:text-2xl',
                              diretoriaToneTextClass(primary.tone)
                            )}
                          >
                            {primary.value}
                          </p>
                        </div>
                      ) : null}
                      <DiretoriaSparkline indicator={ind} />
                      {secondary.map((k) => (
                        <div
                          key={k.label}
                          className="flex items-baseline justify-between gap-2 border-t border-border/60 pt-2 first:border-t-0 first:pt-0"
                        >
                          <span className="min-w-0 shrink text-xs text-muted-foreground">{k.label}</span>
                          <span
                            className={cn(
                              'max-w-[60%] text-right text-sm font-semibold break-words',
                              diretoriaToneTextClass(k.tone)
                            )}
                          >
                            {k.value}
                          </span>
                        </div>
                      ))}
                      <span className="inline-block pt-1 text-xs font-medium text-primary">Ver análise</span>
                    </CardContent>
                  </button>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      <DiretoriaIndicatorAnalysisModal
        indicator={open}
        isOpen={Boolean(open)}
        onClose={() => setOpen(null)}
        sqlProps={open ? resolveIndicatorSql(open) : undefined}
      />
    </>
  );
};
